import { isValid, isWithinInterval, roundToNearestMinutes } from "date-fns";
import { throttle, debounce } from "../utils/Performance";

export default class Listing {
    /**
     * Creates an instance of Listing and initialise component.
     * @param {*} props
     * @memberof Listing
     */
    constructor(props) {
        /**
         * Props currently include:
         * @var {HTMLElement} root The section element containing the filters form. Default `listing-items`
         * @var {String} json_data The ID of the <script> tag containing the JSON data. Default `json-data`.
         * @var {String} card_id The selector identifying the cards elements. Default `[data-id]`.
         * @var {String} daterange The input name to treat as a date range.
         * @var {Function} callback Any method to run after filtering.
         * Elements tagged as [data-interstitial] and [data-featured] will be included in the initial pagination.
         */
        this.el = props.root;
        this.container = props.listing || "listing-items";
        this.callback = props.callback;
        this.daterange = props.daterange || "";
        this.json_data = props.json_data || "json-data";
        this.card_id = props.card_id || "[data-id]";
        this.pagination = props.pagination || false;
        this.interstitial = document
            .getElementById(this.container)
            .querySelectorAll("[data-interstitial], [data-featured]");
        this.no_results = document.querySelector("[data-no-results]");
        this.loading_indicator = document.querySelector("loading-indicator");

        // The filters form
        this.form = this.el.querySelector("form");

        // Make filters section visible
        this.el.setAttribute("aria-hidden", "false");

        // SSR items container
        this.container = document.getElementById(this.container);

        if (this.pagination) {
            this.pagination_block = document.querySelector(this.pagination.el);
        }

        const promo = document.getElementById("whats-on-promo");
        if (promo) {
            this.promo_blocks = {
                events: promo.content.querySelectorAll(".event"),
                programme: promo.content.querySelectorAll(".programme"),
            };
        }

        /**
         * Populate form fields (and components) from URL query string
         */
        if (location.search) {
            let url = new URL(location.href);
            let params = {};
            url.searchParams.forEach((item, index) => {
                params[index] = item;
                let el = document.querySelector(
                    `[name="${index}"][id="${item}"]`,
                );
                if (el && el.type == "radio") {
                    el.checked = true;
                }
                if (
                    ["venue", "access", "genre", "strand", "offer"].includes(
                        index,
                    )
                ) {
                    document
                        .querySelector("#toggle-more-filters")
                        .dispatchEvent(new Event("click", { bubbles: true }));
                }
            });

            document.querySelectorAll("cog-multi-select").forEach((item) => {
                if (Object.keys(params).includes(item.name)) {
                    item.value = params[item.name];
                }
            });

            setTimeout(() => {
                this.loading_indicator.setAttribute("active", false);
            }, 500);
        }

        this.fetchContent(props);
    }

    /**
     * Load HTML and JSON async and updates data on page.
     *
     * @param   {mixed}  props
     *
     * @return  {void}
     */
    async fetchContent(props) {
        let html = "";
        let json = "";

        let cache_data_lifespan = roundToNearestMinutes(new Date(), {
            nearestTo: 15,
            roundingMethod: "ceil",
        });

        if (props.html) {
            html = await fetch(
                `${props.html}?t=${cache_data_lifespan.getTime()}`,
            );
            html = await html.text();

            if (html) {
                const doc = new DOMParser().parseFromString(html, "text/html");

                this.container.innerHTML = "";

                doc.querySelectorAll(this.card_id).forEach((item) => {
                    // Append matching element to container
                    this.container.append(item);
                });

                this.default_listing = this.container.innerHTML;

                if (this.pagination) {
                    this.paginator();
                }
            }
        }

        if (props.json) {
            json = await fetch(
                `${props.json}?t=${cache_data_lifespan.getTime()}`,
            );
            json = await json.json();

            try {
                json = JSON.stringify(json);
                document.getElementById(this.json_data).innerHTML = json;
            } catch (error) {
                // Fail silently
            }
        }

        if (this.loading_indicator) {
            this.loading_indicator.setAttribute("active", false);
        }

        this.init();
    }

    /**
     * Parse JSON data and setup event listeners
     *
     * @return {void}
     */
    init() {
        if (!this.container || !this.form) return;

        // Default items (including promo)
        this.default_listing = this.container.innerHTML;

        // Fetch and parse SSR JSON data
        const json = document.getElementById(this.json_data);

        if (!json) return;

        // Parse JSON data
        this.data = json.text ? JSON.parse(json.text) : [];

        // Filtered items placeholder
        this.filtered = [];

        // SSR HTML card items
        this.cards = this.container.querySelectorAll(this.card_id);
        this.featured = this.container.querySelectorAll(".featured");

        // Callback bound to this class
        const filterItems = this.filterItems.bind(this);

        // data-live="true" will run the filters upon input value changing.
        const live_submit = this.form.dataset.live === "true";

        // Trigger a change event upon resetting the form
        // Resets the listing content to its original state
        this.form.addEventListener("reset", (e) => {
            setTimeout(() => {
                this.container.innerHTML = this.default_listing;
                this.paginator();
                this.no_results?.setAttribute("aria-hidden", "true");
            }, 50);
        });

        // Attach change event to form
        const event = live_submit ? "change" : "submit";
        const filter_items = throttle(filterItems, 250);

        this.form.addEventListener(event, (e) => {
            filter_items();
        });

        filter_items();

        if (this.pagination) {
            this.paginator();
            this.paginator_button();
        }

        document.dispatchEvent(
            new CustomEvent("listingLoaded", {
                detail: { data: this.data, cards: this.cards },
                bubbles: true,
                composed: true,
            }),
        );
    }

    filterItems(e) {
        // Get current form data
        let form_data = new FormData(this.form);

        // Filters status summary
        this.filters_status = {};

        // Build query dictionary
        let query = {};
        for (const pair of form_data.entries()) {
            if (pair[1] && pair[1] != "undefined") {
                query[pair[0]] = form_data.getAll(pair[0]);
            }
        }

        this.query = query;

        // Check if we're in "By Event" view mode - apply HideFromEventsOnly filter only in event view
        const isEventView = document.querySelector('#view-mode .active')?.href?.includes('#event');

        // Filter JSON data
        this.filtered = this.data.filter((item) => {
            // Apply HideFromEventsOnly filter ONLY when in event view (both Shows and All)
            if (isEventView && item.HideFromEventsOnly === true) {
                return false; // Exclude this item from the filtered list
            }

            let match = [];

            if (Object.values(query).length) {
                Object.keys(query).forEach((name) => {
                    
                    if (query[name].includes("")) {
                        match.push(true);
                        return;
                    }

                    // Placeholder for matching nodes
                    let matching = new Set();
                    let isArray = Array.isArray(item[name]) ? true : false;

                    this.filters_status[name] = {};

                    if (this.daterange == name) {
                        // Date range field
                        let [start, end] = query[name].join("").split("/");
                        if (!end) {
                            end = start;
                        }

                        this.filters_status[name] = [start, end];

                        // Find a match
                        let dates = item[name].filter((date) => {
                            return isWithinInterval(date, {
                                start: start,
                                end: end,
                            });
                        });
                        let search = dates.length ? true : false;
                        match.push(search);
                    } else {
                        // Default matching
                        let input_value = query[name];

                        this.filters_status[name] = input_value;

                        // Data provided by third party might be separated by a comma
                        if (input_value[0].indexOf(",") >= 0) {
                            input_value = input_value[0].split(",");
                        }

                        // Create the search Set from query data
                        let search = new Set(input_value);

                        // Create the matching set from JSON data
                        if (isArray) {
                            matching = new Set(item[name]);
                        } else {
                            matching.add(item[name]);
                        }

                        // Find data intersection
                        let intersection = new Set(
                            [...matching].filter((x) => search.has(x)),
                        );

                        intersection = intersection.size ? true : false;
                        match.push(intersection);
                    }
                });
            }

            // Return matching nodes only
            return !match.includes(false);
        });

        // Pluck IDs
        this.filtered = this.filtered.map((item) => item.id);

        // Render filtered items
        this.render();

        // Eventually run the attached callback
        if (this.callback) {
            this.callback();
        }

        // Prevent default form submission
        return false;
    }

    paginator() {
        let items = this.container.querySelectorAll(":scope>li");
        let per_page = this.pagination.per_page + this.interstitial.length;

        let paginator = items.length > per_page;

        if (paginator && this.pagination_block) {
            this.paginate(per_page);
        }
    }

    paginator_button() {
        let button = this.pagination_block.querySelector("button");
        button.addEventListener("click", (e) => {
            let cards = this.container.querySelectorAll('[aria-hidden="true"]');
            [...cards].slice(0, this.pagination.per_page).map((item) => {
                item.setAttribute("aria-hidden", "false");
            });

            cards = this.container.querySelectorAll('[aria-hidden="true"]');
            if (cards.length <= 0) {
                this.pagination_block.setAttribute("aria-hidden", "true");
            }
        });
    }

    paginate(per_page) {
        let items = this.container.querySelectorAll(":scope>li");

        [...items]
            .map((item, index) => {
                let status = index >= per_page;
                item.setAttribute("aria-hidden", status);
                return item;
            })
            .filter((item) => {
                return item.getAttribute("aria-hidden") == "true";
            });

        let paginator = items.length > per_page;
        this.pagination_block.setAttribute("aria-hidden", !paginator);

        let no_results = items.length > 0;
        this.no_results.setAttribute("aria-hidden", no_results);
    }

    render() {
        // Reset container
        this.container.innerHTML = "";

        // Check if we're in event view and need to apply HideFromEventsOnly filter
        const isEventView = document.querySelector('#view-mode .active')?.href?.includes('#event');
        
        // If in event view, always use filtered results even if no other query parameters
        // If in date view, use filtered results only if there are query parameters
        const shouldUseFilteredResults = isEventView || Object.keys(this.query).length > 0;

        if (!shouldUseFilteredResults) {
            this.container.innerHTML = this.default_listing;
            this.container.classList.remove("filtered");
        } else {
            this.container.classList.add("filtered");

            let cards = [];

            // Sort relevant items
            [...this.cards]
                .sort((a, b) => {
                    return (
                        this.filtered.indexOf(a.dataset.id) -
                        this.filtered.indexOf(b.dataset.id)
                    );
                })
                .forEach((item) => {
                    // Append matching element to container
                    if (this.filtered.includes(item.dataset.id)) {
                        cards.push(item);
                    }
                });

            if (
                this.query &&
                Object.keys(this.query).length == 1 &&
                Object.keys(this.query).includes("type")
            ) {
                let type = this.query.type;
                let promo = [];

                if (type.includes("programme")) {
                    promo = this.promo_blocks.programme;
                } else {
                    promo = this.promo_blocks.events;
                }

                var position = 5;
                const offset = 6;
                [...promo].forEach((item, index) => {
                    if (position <= cards.length) {
                        // Check if this promo item should be hidden in event view
                        const promoId = item.getAttribute("data-id");
                        const promoData = this.data.find(dataItem => dataItem.id === promoId);
                        
                        // Skip promo items that should be hidden in event view
                        if (isEventView && promoData && promoData.HideFromEventsOnly === true) {
                            return; // Skip this promo item
                        }
                        
                        cards.splice(position, 0, item);
                        position += offset;

                        // Instead of cloning the <li>, clone its .card child and insert into a new <li>
                        const card = item.querySelector(".card");
                        if (card) {
                            // Also check the cloned card
                            if (isEventView && promoData && promoData.HideFromEventsOnly === true) {
                                return; // Skip the clone too
                            }
                            
                            const li = document.createElement("li");
                            li.setAttribute(
                                "data-id",
                                item.getAttribute("data-id"),
                            );
                            li.className = item.className; // preserve classes
                            li.appendChild(card.cloneNode(true));
                            cards.splice(position, 0, li);
                            position += offset;
                        }
                    }
                });
            }

            cards.forEach((item) => {
                this.container.append(item);
            });
        }

        // Pagination
        if (this.pagination) {
            this.paginate(this.pagination.per_page, this);
        }
    }
}